import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Helmet from '../../common/Helmet';
import { WebsiteUrl, PercentageEndPoint } from '../../common/Constants';
import PercentageCalculator from './percentageCalculator';
import { AdsLeftDesktopFirst, AdsAboveCalcMobile } from "../../common/Ads";
import { isMobile } from 'react-device-detect';

const CommonFooter = lazy(() => import('../../common/Footer'));

// Base meta data for the page
const BASE_TITLE = 'Calcular';
const BASE_DESCRIPTION = 'Aprende a calcular porcentajes fácilmente.';
const BASE_KEYWORDS = 'calculadora de porcentajes, calcular porcentaje, herramienta de calculadora de porcentajes';

const numberToSpanish = {
    1: 'Uno',
    2: 'Dos',
    3: 'Tres',
    4: 'Cuatro',
    5: 'Cinco',
    6: 'Seis',
    7: 'Siete',
    8: 'Ocho',
    9: 'Nueve',
    10: 'Diez',
    11: 'Once',
    12: 'Doce',
    13: 'Trece',
    14: 'Catorce',
    15: 'Quince',
    16: 'Dieciséis',
    17: 'Diecisiete',
    18: 'Dieciocho',
    19: 'Diecinueve',
    20: 'Veinte',
    21: 'Veintiuno',
    22: 'Veintidós',
    23: 'Veintitrés',
    24: 'Veinticuatro',
    25: 'Veinticinco',
    26: 'Veintiséis',
    27: 'Veintisiete',
    28: 'Veintiocho',
    29: 'Veintinueve',
    30: 'Treinta',
    31: 'Treinta y Uno',
    32: 'Treinta y Dos',
    33: 'Treinta y Tres',
    34: 'Treinta y Cuatro',
    35: 'Treinta y Cinco',
    36: 'Treinta y Seis',
    37: 'Treinta y Siete',
    38: 'Treinta y Ocho',
    39: 'Treinta y Nueve',
    40: 'Cuarenta',
    41: 'Cuarenta y Uno',
    42: 'Cuarenta y Dos',
    43: 'Cuarenta y Tres',
    44: 'Cuarenta y Cuatro',
    45: 'Cuarenta y Cinco',
    46: 'Cuarenta y Seis',
    47: 'Cuarenta y Siete',
    48: 'Cuarenta y Ocho',
    49: 'Cuarenta y Nueve',
    50: 'Cincuenta',
    51: 'Cincuenta y Uno',
    52: 'Cincuenta y Dos',
    53: 'Cincuenta y Tres',
    54: 'Cincuenta y Cuatro',
    55: 'Cincuenta y Cinco',
    56: 'Cincuenta y Seis',
    57: 'Cincuenta y Siete',
    58: 'Cincuenta y Ocho',
    59: 'Cincuenta y Nueve',
    60: 'Sesenta',
    61: 'Sesenta y Uno',
    62: 'Sesenta y Dos',
    63: 'Sesenta y Tres',
    64: 'Sesenta y Cuatro',
    65: 'Sesenta y Cinco',
    66: 'Sesenta y Seis',
    67: 'Sesenta y Siete',
    68: 'Sesenta y Ocho',
    69: 'Sesenta y Nueve',
    70: 'Setenta',
    71: 'Setenta y Uno',
    72: 'Setenta y Dos',
    73: 'Setenta y Tres',
    74: 'Setenta y Cuatro',
    75: 'Setenta y Cinco',
    76: 'Setenta y Seis',
    77: 'Setenta y Siete',
    78: 'Setenta y Ocho',
    79: 'Setenta y Nueve',
    80: 'Ochenta',
    81: 'Ochenta y Uno',
    82: 'Ochenta y Dos',
    83: 'Ochenta y Tres',
    84: 'Ochenta y Cuatro',
    85: 'Ochenta y Cinco',
    86: 'Ochenta y Seis',
    87: 'Ochenta y Siete',
    88: 'Ochenta y Ocho',
    89: 'Ochenta y Nueve',
    90: 'Noventa',
    91: 'Noventa y Uno',
    92: 'Noventa y Dos',
    93: 'Noventa y Tres',
    94: 'Noventa y Cuatro',
    95: 'Noventa y Cinco',
    96: 'Noventa y Seis',
    97: 'Noventa y Siete',
    98: 'Noventa y Ocho',
    99: 'Noventa y Nueve',
};

// Convert number to Spanish words
function getSpanishNumber(number) {
  if (number <= 0) return 'Número inválido';
  if (number <= 20) return numberToSpanish[number] || `Número ${number} no encontrado`;

  if (number < 100) {
      const tens = Math.floor(number / 10) * 10;
      const units = number % 10;

      const tensWord = numberToSpanish[tens] || '';
      const unitsWord = numberToSpanish[units] || '';

      if (units === 0) {
          return tensWord;
      } else if (unitsWord) {
          return `${unitsWord} y ${tensWord}`;
      } else {
          return `Número ${number} no encontrado`;
      }
  }

  return numberToSpanish[100] || 'Número 100 no encontrado';
}

// Percentage Converter Component
function PercentageConverter() {
  const { percentage } = useParams();
  const validPercentage = Number(percentage);
  const [amount, setAmount] = useState(100);
  const [result, setResult] = useState(amount * (validPercentage / 100));
  const spanishPercentage = getSpanishNumber(validPercentage);

  useEffect(() => {
      setResult(amount * (validPercentage / 100));
  }, [validPercentage, amount]);

  const TITLE = `${BASE_TITLE} ${validPercentage}% de una Cantidad - Calculadora de ${validPercentage} Porciento`;
  const TITLE_H2 = `Calculadora de Porcentajes ${validPercentage}% de Valor o Cantidad`;

  const DESCRIPTION = `${BASE_DESCRIPTION} Calcula ${validPercentage}% de Cantidad - ${spanishPercentage} Calculadora de Porciento - Descubre cuánto es ${spanishPercentage} por ciento. Esta calculadora determina ${validPercentage}% del valor y porcentaje de una cantidad específica o valor.`;

  const KEYWORDS = `${BASE_KEYWORDS}, ${validPercentage}%, cálculo de ${validPercentage}%, calculadora de ${validPercentage}%, cómo calcular ${validPercentage}%, ${validPercentage}% de cantidad, cómo calcular ${validPercentage} por ciento, cálculo de ${validPercentage}% de valor, ${validPercentage}% calculadora, aprender ${validPercentage}%, explicación de ${validPercentage}%, cómo calcular ${validPercentage}%, porcentaje ${validPercentage} de valor, ${validPercentage} porcentaje, mejor calculadora ${validPercentage}%, cálculo de ${validPercentage}%, métodos de cálculo de porcentajes, explicación de cálculo de ${validPercentage}%, calculadora de ${validPercentage}% de cantidad, calculadora de porcentajes en español`;
  const lang = "es";
  const url = WebsiteUrl;
  const OG_URL = `${WebsiteUrl}/${lang}/${PercentageEndPoint}/${validPercentage}`;
  const href = lang === 'ar' ? '/' : `/${lang}/`;

  return (
      <Container>
          <Helmet
              TITLE={TITLE}
              WEBSITE_URL={url}
              DESCRIPTION={DESCRIPTION}
              KEYWORDS={KEYWORDS}
              OG_URL={OG_URL}
              LANG={lang}
          />
          <Row>
              <Col xs={12} md={9} lg={8}>
                  <div className="mainBackground text-white text-center" style={{ height: '55px' }}>
                      <h1 style={{ fontSize: '17px' }}>{TITLE}</h1>
                  </div>
                  <div className="border border-light">
                      <Row>
                          <div>
                              <p>Para calcular fácilmente el {validPercentage}% de cualquier cantidad ({spanishPercentage} por ciento), sigue estos pasos:</p>
                              <ul>
                                  <li>Comienza convirtiendo el porcentaje a una fracción. {validPercentage}% se puede representar como la fracción: {validPercentage}/100.</li>
                                  <li>Multiplica el valor original por esta fracción. Por ejemplo, si tienes un valor de {amount} y quieres calcular el {validPercentage}% de él, utiliza la siguiente fórmula:
                                      <br />
                                      <strong>Porcentaje = (Valor Original × {validPercentage}/100)</strong>
                                  </li>
                                  <li>Una vez que realices el cálculo, el resultado será el {validPercentage}% del monto original.</li>
                              </ul>
                              <p>Por ejemplo, si el valor original es 200 y quieres calcular el {validPercentage}% de él:</p>
                              <ul>
                                  <li>Convierte el {validPercentage}% a una fracción: {validPercentage}/100.</li>
                                  <li>Multiplica 200 × {validPercentage}/100 = {(200 * validPercentage) / 100}.</li>
                                  <li>El resultado final es {(200 * validPercentage) / 100}, que representa el {validPercentage}% de 200.</li>
                              </ul>
                              <p>Usando este método, puedes calcular fácilmente el {validPercentage}% de cualquier cantidad que desees.</p>
                              <p>Si quieres realizar estos cálculos de forma rápida y fácil, puedes usar la <strong>Calculadora de Porcentajes</strong> a continuación. Esta calculadora proporciona una herramienta efectiva para determinar con precisión el porcentaje de cualquier valor. ¡Úsala ahora para simplificar tus cálculos!</p>
                          </div>
                      </Row>
                      <Row className='calcualtorStyle'>
                          <div className='p-3 mb-2 mainBackground text-white' style={{ textAlign: 'center' }}>
                              <h2 style={{ fontSize: '16px' }}>{TITLE_H2}</h2>
                          </div>
                          <hr />
                          <PercentageCalculator percentage={validPercentage} />
                          <hr />
                          <Suspense fallback={<div>Cargando...</div>}>
                              <CommonFooter lang={lang} url={OG_URL} textName="Citación" />
                          </Suspense>
                          <br />
                          <br />
                      </Row>
                      {isMobile &&
                          <>
                              <hr />
                              <Row>
                                  <AdsAboveCalcMobile />
                                  <br />
                              </Row>
                          </>
                      }
                      <hr />
                      <Row>
                          <div className='alert alert-info'>
                              También puedes usar la <a href={href}>Calculadora de Porcentajes</a> para calcular porcentajes de cualquier número y determinar aumentos y disminuciones de porcentaje.
                          </div>
                          <hr />
                      </Row>
                  </div>
              </Col>
              <Col xs={12} md={3} lg={4}>
                  {!isMobile && <AdsLeftDesktopFirst />}
              </Col>
          </Row>
      </Container>
  );
}

export default PercentageConverter;
